import React, { useEffect } from 'react'
import { Box, Container, Typography, Avatar, AvatarGroup } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useDispatch, useSelector } from 'react-redux'
import { fetchGetJobCount } from 'src/redux/home'
import HomepageAbstract from 'static/images/Homepage Abstract.png'
import MUSCLogo from 'static/images/MUSC_logo.png'
import SBLLogo from 'static/images/SBL.logo.jpg'
import AraImage from 'static/images/ara.jpeg'
import AlekseyImage from 'static/images/aleksey.jpeg'
import IrinaImage from 'static/images/irina.jpeg'
import BrockImage from 'static/images/brock.jpeg'

const StatsShowcase = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const totalJobsCount = useSelector(state => state.home.jobCount || 0)

  // Fetch total jobs count using the dedicated endpoint
  useEffect(() => {
    dispatch(fetchGetJobCount())
  }, [dispatch])

  return (
    <Box
      sx={{
        py: 8,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: { xs: 'flex', md: 'grid' },
            flexDirection: { xs: 'column', md: 'unset' },
            gridTemplateColumns: { md: 'repeat(9, 1fr)' },
            gridTemplateRows: { md: 'repeat(6, 80px)' },
            gap: { xs: 2, md: 1 },
            minHeight: { xs: 'auto', md: '500px' },
            position: 'relative'
          }}
        >
          {/* Top Left - Founded by Medical Providers */}
          <Box
            sx={{
              gridColumn: { md: '1 / 4' },
              gridRow: { md: '1 / 3' },
              order: { xs: 1, md: 'unset' },
              backgroundColor: 'white',
              borderRadius: '20px',
              p: 3,
              boxShadow: theme.palette.shadows.medium,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.grey[900], mb: 1, fontSize: '0.9rem' }}>
              Founded by Medical Providers and Technologists
            </Typography>
            <AvatarGroup max={4} sx={{ justifyContent: 'center', mb: 1 }}>
              <Avatar src={AraImage} sx={{ width: 28, height: 28 }}>A</Avatar>
              <Avatar src={AlekseyImage} sx={{ width: 28, height: 28 }}>A</Avatar>
              <Avatar src={IrinaImage} sx={{ width: 28, height: 28 }}>I</Avatar>
              <Avatar src={BrockImage} sx={{ width: 28, height: 28 }}>B</Avatar>
            </AvatarGroup>
            <Typography variant="body2" sx={{ color: theme.palette.grey[900], fontSize: '0.75rem', lineHeight: 1.2 }}>
              Security/privacy assurances
            </Typography>
          </Box>

          {/* Top Center - Total Jobs */}
          <Box
            sx={{
              gridColumn: { md: '4 / 7' },
              gridRow: { md: '1 / 2' },
              order: { xs: 2, md: 'unset' },
              backgroundColor: theme.palette.background.paper,
              borderRadius: '20px',
              p: 3,
              boxShadow: theme.palette.shadows.medium,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center'
            }}
          >
            <Box>
              <Typography variant="h2" sx={{ fontWeight: 700, color: theme.palette.grey[900], fontSize: '1.8rem', lineHeight: 1 }}>
                {totalJobsCount.toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.grey[900], fontSize: '0.8rem' }}>
                Active Jobs
              </Typography>
            </Box>
          </Box>

          {/* Top Right - Growth Stats */}
          <Box
            sx={{
              gridColumn: { md: '7 / 10' },
              gridRow: { md: '1 / 3' },
              order: { xs: 3, md: 'unset' },
              backgroundColor: theme.palette.success.light,
              borderRadius: '20px',
              p: 3,
              boxShadow: theme.palette.shadows.medium,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center'
            }}
          >
            <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.grey[900], fontSize: '1.5rem', mb: 0.5 }}>
              3k+
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.grey[900], fontSize: '0.8rem' }}>
              New Users
            </Typography>
          </Box>

          {/* Left - Trusted Platform */}
          <Box
            sx={{
              gridColumn: { md: '1 / 4' },
              gridRow: { md: '3 / 5' },
              order: { xs: 4, md: 'unset' },
              backgroundColor: theme.palette.info.light,
              borderRadius: '20px',
              p: 3,
              boxShadow: theme.palette.shadows.medium,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.grey[900], mb: 1, fontSize: '1rem' }}>
              Trusted Platform
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.grey[900], fontSize: '0.8rem', lineHeight: 1.3 }}>
              Connecting healthcare professionals with opportunities that matter, built by clinicians for clinicians.
            </Typography>
          </Box>

          {/* Center Image - Healthcare Professional */}
          <Box
            sx={{
              gridColumn: { md: '4 / 7' },
              gridRow: { md: '2 / 5' },
              order: { xs: 5, md: 'unset' },
              backgroundColor: theme.palette.success.light,
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: theme.palette.shadows.medium,
              p: 3
            }}
          >
            <Box
              component="img"
              src={HomepageAbstract}
              alt="Healthcare Professional"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </Box>

          {/* Right - Network Stats */}
          <Box
            sx={{
              gridColumn: { md: '7 / 10' },
              gridRow: { md: '3 / 5' },
              order: { xs: 6, md: 'unset' },
              backgroundColor: theme.palette.warning.light,
              borderRadius: '20px',
              p: 3,
              boxShadow: theme.palette.shadows.medium,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center'
            }}
          >
            <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.grey[900], fontSize: '1.5rem', mb: 0.5 }}>
              500+
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.grey[900], fontSize: '0.8rem', mb: 1 }}>
              Hospitals Listed
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.warning.main, fontSize: '0.8rem', fontWeight: 600 }}>
              Nationwide Coverage
            </Typography>
          </Box>

          {/* Bottom Left - MUSC Logo */}
          <Box
            sx={{
              gridColumn: { md: '1 / 4' },
              gridRow: { md: '5 / 7' },
              order: { xs: 7, md: 'unset' },
              backgroundColor: 'white',
              borderRadius: '20px',
              p: 3,
              boxShadow: theme.palette.shadows.medium,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box
              component="img"
              src={MUSCLogo}
              alt="MUSC Health Logo"
              sx={{
                maxWidth: '90%',
                maxHeight: '40px',
                objectFit: 'contain'
              }}
            />
          </Box>

          {/* Bottom Center - Success Rate */}
          <Box
            sx={{
              gridColumn: { md: '4 / 7' },
              gridRow: { md: '5 / 7' },
              order: { xs: 8, md: 'unset' },
              backgroundColor: theme.palette.secondary.light,
              borderRadius: '20px',
              p: 3,
              boxShadow: theme.palette.shadows.medium,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center'
            }}
          >
            <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.grey[900], fontSize: '1.5rem', mb: 0.5 }}>
              $0
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.grey[900], fontSize: '0.8rem' }}>
              Placement Fees
            </Typography>
          </Box>

          {/* Bottom Right - SBL Logo */}
          <Box
            sx={{
              gridColumn: { md: '7 / 10' },
              gridRow: { md: '5 / 7' },
              order: { xs: 9, md: 'unset' },
              backgroundColor: 'white',
              borderRadius: '20px',
              p: 3,
              boxShadow: theme.palette.shadows.medium,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box
              component="img"
              src={SBLLogo}
              alt="Sarah Bush Lincoln Logo"
              sx={{
                maxWidth: '90%',
                maxHeight: '40px',
                objectFit: 'contain'
              }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

export default StatsShowcase
